/**
 * Опциональная векторная подложка MapLibre из локального Martin (PMTiles/MBTiles).
 * Появляется в switcher только при VITE_MARTIN_URL + (TILESET | STYLE_URL).
 */

import type { StyleSpecification } from "maplibre-gl";

export const MARTIN_BASE_LAYER_ID = "martin";

export type MartinLayerProfile = "openmaptiles" | "protomaps";

export type MartinBasemapEnv = {
  baseUrl: string;
  tileset: string | null;
  styleUrl: string | null;
  profile: MartinLayerProfile;
};

function trimSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

export function getMartinBasemapEnv(): MartinBasemapEnv | null {
  const raw = String(import.meta.env.VITE_MARTIN_URL || "").trim();
  if (!raw) return null;
  const baseUrl = trimSlash(raw);
  const tileset = String(import.meta.env.VITE_MARTIN_TILESET || "").trim() || null;
  const styleUrl = String(import.meta.env.VITE_MARTIN_STYLE_URL || "").trim() || null;
  if (!tileset && !styleUrl) return null;
  const profileRaw = String(import.meta.env.VITE_MARTIN_LAYER_PROFILE || "openmaptiles")
    .trim()
    .toLowerCase();
  const profile: MartinLayerProfile =
    profileRaw === "protomaps" ? "protomaps" : "openmaptiles";
  return { baseUrl, tileset, styleUrl, profile };
}

export function isMartinBasemapConfigured(): boolean {
  return getMartinBasemapEnv() != null;
}

function martinTileUrl(baseUrl: string, tileset: string): string {
  return `${baseUrl}/${encodeURIComponent(tileset)}/{z}/{x}/{y}`;
}

function martinGlyphs(baseUrl: string): string {
  return `${baseUrl}/font/{fontstack}/{range}`;
}

function martinSprite(baseUrl: string): string {
  return `${baseUrl}/sprite/sprite`;
}

/** Минимальный стиль OpenMapTiles (совместим с типичными extracts под Martin). */
function buildOpenMapTilesStyle(baseUrl: string, tileset: string): StyleSpecification {
  const source = "martin";
  return {
    version: 8,
    name: "Martin OpenMapTiles",
    glyphs: martinGlyphs(baseUrl),
    sprite: martinSprite(baseUrl),
    sources: {
      [source]: {
        type: "vector",
        tiles: [martinTileUrl(baseUrl, tileset)],
        minzoom: 0,
        maxzoom: 14,
      },
    },
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": "#f0f0e8" },
      },
      {
        id: "water",
        type: "fill",
        source,
        "source-layer": "water",
        paint: { "fill-color": "#a0c8f0" },
      },
      {
        id: "landcover",
        type: "fill",
        source,
        "source-layer": "landcover",
        paint: { "fill-color": "#d8e8c8", "fill-opacity": 0.6 },
      },
      {
        id: "park",
        type: "fill",
        source,
        "source-layer": "park",
        paint: { "fill-color": "#c8e0b0", "fill-opacity": 0.5 },
      },
      {
        id: "building",
        type: "fill",
        source,
        "source-layer": "building",
        minzoom: 13,
        paint: { "fill-color": "#d0cdc8", "fill-opacity": 0.85 },
      },
      {
        id: "transportation",
        type: "line",
        source,
        "source-layer": "transportation",
        paint: {
          "line-color": "#888880",
          "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.4, 14, 2.2],
        },
      },
      {
        id: "boundary",
        type: "line",
        source,
        "source-layer": "boundary",
        paint: { "line-color": "#9a8a7a", "line-width": 0.8, "line-dasharray": [2, 1] },
      },
      {
        id: "place-label",
        type: "symbol",
        source,
        "source-layer": "place",
        layout: {
          "text-field": ["coalesce", ["get", "name:ru"], ["get", "name"], ["get", "name:en"]],
          "text-size": 12,
          "text-font": ["Open Sans Regular", "Noto Sans Regular"],
        },
        paint: {
          "text-color": "#333328",
          "text-halo-color": "#f0f0e8",
          "text-halo-width": 1.2,
        },
      },
    ],
  };
}

/**
 * Встроенный стиль Protomaps basemaps v4 (светлая «paper»-палитра).
 * Различает kind у landcover / landuse / roads / waterway / places.
 * POI icons не требуются (минимальные Martin sprites не совпадают с basemaps-assets).
 */
export function buildProtomapsStyle(baseUrl: string, tileset: string): StyleSpecification {
  const source = "martin";
  const fonts = ["Open Sans Regular", "Noto Sans Regular"];

  return {
    version: 8,
    name: "Martin Protomaps",
    glyphs: martinGlyphs(baseUrl),
    sprite: martinSprite(baseUrl),
    sources: {
      [source]: {
        type: "vector",
        tiles: [martinTileUrl(baseUrl, tileset)],
        minzoom: 0,
        maxzoom: 15,
      },
    },
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": "#f2efe9" },
      },
      {
        id: "earth",
        type: "fill",
        source,
        "source-layer": "earth",
        paint: { "fill-color": "#f2efe9" },
      },
      {
        id: "landcover-wood",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "wood"],
        paint: { "fill-color": "#b6d0a0", "fill-opacity": 0.65 },
      },
      {
        id: "landcover-scrub",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "scrub"],
        paint: { "fill-color": "#c9d9a8", "fill-opacity": 0.55 },
      },
      {
        id: "landcover-grassland",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["in", ["get", "kind"], ["literal", ["grassland", "grass"]]],
        paint: { "fill-color": "#d4e4b8", "fill-opacity": 0.55 },
      },
      {
        id: "landcover-farmland",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "farmland"],
        paint: { "fill-color": "#e4e0b0", "fill-opacity": 0.5 },
      },
      {
        id: "landcover-wetland",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "wetland"],
        paint: { "fill-color": "#b8d4c8", "fill-opacity": 0.55 },
      },
      {
        id: "landcover-glacier",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "glacier"],
        paint: { "fill-color": "#e8f0f4", "fill-opacity": 0.85 },
      },
      {
        id: "landcover-barren",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "barren"],
        paint: { "fill-color": "#e8dfc8", "fill-opacity": 0.45 },
      },
      {
        id: "landcover-urban",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: ["==", ["get", "kind"], "urban_area"],
        paint: { "fill-color": "#e0dbd4", "fill-opacity": 0.5 },
      },
      {
        id: "landcover-other",
        type: "fill",
        source,
        "source-layer": "landcover",
        filter: [
          "!",
          [
            "in",
            ["get", "kind"],
            [
              "literal",
              [
                "wood",
                "scrub",
                "grassland",
                "grass",
                "farmland",
                "wetland",
                "glacier",
                "barren",
                "urban_area",
              ],
            ],
          ],
        ],
        paint: { "fill-color": "#c6ddaa", "fill-opacity": 0.4 },
      },
      {
        id: "landuse-park",
        type: "fill",
        source,
        "source-layer": "landuse",
        filter: ["in", ["get", "kind"], ["literal", ["park", "forest", "golf_course", "garden"]]],
        paint: { "fill-color": "#c8e0b0", "fill-opacity": 0.5 },
      },
      {
        id: "landuse-residential",
        type: "fill",
        source,
        "source-layer": "landuse",
        filter: ["==", ["get", "kind"], "residential"],
        paint: { "fill-color": "#e8e2da", "fill-opacity": 0.45 },
      },
      {
        id: "landuse-industrial",
        type: "fill",
        source,
        "source-layer": "landuse",
        filter: ["in", ["get", "kind"], ["literal", ["industrial", "commercial", "retail"]]],
        paint: { "fill-color": "#ddd6cc", "fill-opacity": 0.45 },
      },
      {
        id: "landuse-other",
        type: "fill",
        source,
        "source-layer": "landuse",
        filter: [
          "!",
          [
            "in",
            ["get", "kind"],
            [
              "literal",
              [
                "park",
                "forest",
                "golf_course",
                "garden",
                "residential",
                "industrial",
                "commercial",
                "retail",
              ],
            ],
          ],
        ],
        paint: { "fill-color": "#e8dfc8", "fill-opacity": 0.3 },
      },
      {
        id: "water",
        type: "fill",
        source,
        "source-layer": "water",
        paint: { "fill-color": "#80bde0" },
      },
      {
        id: "waterway-river",
        type: "line",
        source,
        "source-layer": "waterway",
        filter: ["in", ["get", "kind"], ["literal", ["river", "canal"]]],
        paint: {
          "line-color": "#80bde0",
          "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.8, 14, 3],
        },
      },
      {
        id: "waterway-stream",
        type: "line",
        source,
        "source-layer": "waterway",
        filter: ["in", ["get", "kind"], ["literal", ["stream", "drain", "ditch", "other"]]],
        minzoom: 11,
        paint: {
          "line-color": "#90c8e4",
          "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.4, 15, 1.4],
        },
      },
      {
        id: "roads-path",
        type: "line",
        source,
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "path"],
        minzoom: 13,
        paint: {
          "line-color": "#b0a898",
          "line-width": ["interpolate", ["linear"], ["zoom"], 13, 0.4, 16, 1.2],
          "line-dasharray": [2, 1.5],
        },
      },
      {
        id: "roads-minor",
        type: "line",
        source,
        "source-layer": "roads",
        filter: ["in", ["get", "kind"], ["literal", ["minor_road", "other"]]],
        paint: {
          "line-color": "#c4bfb6",
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.4, 16, 2.2],
        },
      },
      {
        id: "roads-medium",
        type: "line",
        source,
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "medium_road"],
        paint: {
          "line-color": "#b8b0a4",
          "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.6, 14, 3.2],
        },
      },
      {
        id: "roads-major",
        type: "line",
        source,
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "major_road"],
        paint: {
          "line-color": "#d4a574",
          "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.8, 14, 4],
        },
      },
      {
        id: "roads-highway",
        type: "line",
        source,
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "highway"],
        paint: {
          "line-color": "#e09850",
          "line-width": ["interpolate", ["linear"], ["zoom"], 5, 1, 14, 5.5],
        },
      },
      {
        id: "roads-rail",
        type: "line",
        source,
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "rail"],
        minzoom: 10,
        paint: {
          "line-color": "#9a9088",
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.6, 14, 1.6],
          "line-dasharray": [3, 2],
        },
      },
      {
        id: "buildings",
        type: "fill",
        source,
        "source-layer": "buildings",
        minzoom: 13,
        paint: { "fill-color": "#d9d3c9", "fill-opacity": 0.85 },
      },
      {
        id: "buildings-outline",
        type: "line",
        source,
        "source-layer": "buildings",
        minzoom: 14,
        paint: { "line-color": "#c4bdb2", "line-width": 0.4 },
      },
      {
        id: "boundaries",
        type: "line",
        source,
        "source-layer": "boundaries",
        paint: {
          "line-color": "#9a8f82",
          "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.4, 10, 1],
          "line-dasharray": [3, 2],
        },
      },
      {
        id: "places-country",
        type: "symbol",
        source,
        "source-layer": "places",
        filter: ["==", ["get", "kind"], "country"],
        layout: {
          "text-field": ["coalesce", ["get", "name"], ["get", "pname"]],
          "text-size": ["interpolate", ["linear"], ["zoom"], 2, 11, 6, 16],
          "text-font": fonts,
          "text-transform": "uppercase",
          "text-letter-spacing": 0.08,
        },
        paint: {
          "text-color": "#5a554c",
          "text-halo-color": "#f2efe9",
          "text-halo-width": 1.4,
        },
      },
      {
        id: "places-region",
        type: "symbol",
        source,
        "source-layer": "places",
        filter: ["==", ["get", "kind"], "region"],
        minzoom: 4,
        layout: {
          "text-field": ["coalesce", ["get", "name"], ["get", "pname"]],
          "text-size": ["interpolate", ["linear"], ["zoom"], 4, 10, 9, 14],
          "text-font": fonts,
        },
        paint: {
          "text-color": "#4a453c",
          "text-halo-color": "#f2efe9",
          "text-halo-width": 1.2,
        },
      },
      {
        id: "places-locality",
        type: "symbol",
        source,
        "source-layer": "places",
        filter: ["in", ["get", "kind"], ["literal", ["locality", "neighbourhood", "macrohood"]]],
        minzoom: 8,
        layout: {
          "text-field": ["coalesce", ["get", "name"], ["get", "pname"]],
          "text-size": ["interpolate", ["linear"], ["zoom"], 8, 10, 14, 14],
          "text-font": fonts,
        },
        paint: {
          "text-color": "#2a2a24",
          "text-halo-color": "#f2efe9",
          "text-halo-width": 1.2,
        },
      },
      {
        id: "places-other",
        type: "symbol",
        source,
        "source-layer": "places",
        filter: [
          "!",
          [
            "in",
            ["get", "kind"],
            ["literal", ["country", "region", "locality", "neighbourhood", "macrohood"]],
          ],
        ],
        minzoom: 10,
        layout: {
          "text-field": ["coalesce", ["get", "name"], ["get", "pname"]],
          "text-size": 11,
          "text-font": fonts,
        },
        paint: {
          "text-color": "#3a3830",
          "text-halo-color": "#f2efe9",
          "text-halo-width": 1,
        },
      },
    ],
  };
}

export function buildMartinBuiltinStyle(env: MartinBasemapEnv): StyleSpecification | null {
  if (!env.tileset) return null;
  if (env.profile === "protomaps") {
    return buildProtomapsStyle(env.baseUrl, env.tileset);
  }
  return buildOpenMapTilesStyle(env.baseUrl, env.tileset);
}

/**
 * Внешний http(s) glyphs/sprite → локальный Martin.
 * Same-origin пути (`/styles/...`) оставляем: офлайн theme pack (PBF + PNG sheets).
 */
function rewriteAssetUrlToMartin(
  url: string | undefined,
  martinUrl: string,
): string | undefined {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return martinUrl;
  return url;
}

/** Подмена glyphs/sprite и vector tile URL на текущий Martin (для внешнего style JSON). */
export function rewriteStyleForMartin(
  style: StyleSpecification,
  env: MartinBasemapEnv,
): StyleSpecification {
  const next: StyleSpecification = {
    ...style,
    glyphs: rewriteAssetUrlToMartin(
      typeof style.glyphs === "string" ? style.glyphs : undefined,
      martinGlyphs(env.baseUrl),
    ),
    sprite: rewriteAssetUrlToMartin(
      typeof style.sprite === "string" ? style.sprite : undefined,
      martinSprite(env.baseUrl),
    ),
    sources: { ...style.sources },
  };
  if (env.tileset) {
    const tileUrl = martinTileUrl(env.baseUrl, env.tileset);
    for (const [id, src] of Object.entries(next.sources ?? {})) {
      if (src && typeof src === "object" && "type" in src && src.type === "vector") {
        next.sources![id] = {
          ...src,
          tiles: [tileUrl],
          // убираем url: pmtiles://… — в контуре только тайлы через Martin
          url: undefined,
        } as typeof src;
      }
    }
  }
  return next;
}

export async function resolveMartinStyle(env: MartinBasemapEnv): Promise<StyleSpecification> {
  if (env.styleUrl) {
    const res = await fetch(env.styleUrl);
    if (!res.ok) {
      throw new Error(`Не удалось загрузить стиль Martin: HTTP ${res.status}`);
    }
    const json = (await res.json()) as StyleSpecification;
    return rewriteStyleForMartin(json, env);
  }
  const builtin = buildMartinBuiltinStyle(env);
  if (!builtin) {
    throw new Error("Задайте VITE_MARTIN_TILESET или VITE_MARTIN_STYLE_URL");
  }
  return builtin;
}
